import { useEffect, useState } from 'react';

/**
 * Latches true the first time anything on the page scrolls.
 *
 * Scroll events don't bubble, so this listens in the capture phase on
 * `document` to catch scrolling from any container.
 *
 * Useful for hiding fixed-position elements anchored to a viewport point
 * captured earlier: once a scroll container moves, that anchor is stale and the
 * element would be drawn in the wrong place.
 *
 * No minimum distance on purpose: a threshold needs the container's position
 * from before the scroll, which the first event — the one that matters — is the
 * only source of, so instant jumps (`scrollTo`, `scrollIntoView`) would slip
 * through. Latching on any scroll is the fail-safe direction.
 *
 * @returns False initially, true once anything has scrolled. Never goes back —
 * mount a fresh instance (`key`) per anchor.
 */
export const useScrolling = (): boolean => {
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    const options = { capture: true, passive: true };

    const onScroll = () => {
      setHasScrolled(true);
      document.removeEventListener('scroll', onScroll, options);
    };

    document.addEventListener('scroll', onScroll, options);
    return () => document.removeEventListener('scroll', onScroll, options);
  }, []);

  return hasScrolled;
};
